import { useCallback, useMemo, useState } from 'react'

import type Decimal from 'decimal.js-light'
import DecimalJS from 'decimal.js-light'

import { useAdminFund } from '@repo/common/queries/admin/fund'

import {
  type AdminUserData,
  useAdminUsersByWallet,
} from './useAdminUsersByWallet'
import { useTokenHolders } from './useTokenHolders'

const INITIAL_ITEMS_PER_PAGE = 100
const LOAD_MORE_ITEMS = 100

export interface FundHolder {
  id: string
  name: string | null
  walletAddress: string
  totalShares: DecimalJS
  totalValue: DecimalJS
  totalTransactions: number
  firstTransactionAt: string
  lastTransactionAt: string
  profileUrl: string | null
  isVault: boolean
}

export interface FundRegistryStats {
  totalShares: DecimalJS
  totalHolders: number
  totalFrozenTokens: number
  totalExceptionCases: number
  currentPrice: DecimalJS
}

export interface FundRegistryData {
  stats: FundRegistryStats
  holders: FundHolder[]
}

export interface FundRegistrySearchParams {
  searchTerm: string
  sortBy:
    | 'totalShares'
    | 'totalValue'
    | 'totalTransactions'
    | 'name'
    | 'firstTransactionAt'
  sortOrder: 'asc' | 'desc'
}

export interface FundRegistryPagination {
  currentPage: number
  itemsPerPage: number
  totalItems: number
  hasMore: boolean
}

export interface UseFundRegistryParams {
  effective_at?: Date
}

export interface UseFundRegistryResult {
  data: FundRegistryData | null
  holders: FundHolder[]
  stats: FundRegistryStats | null
  pagination: FundRegistryPagination
  searchParams: FundRegistrySearchParams
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => Promise<void>
  loadMore: () => void
  updateSearch: (searchTerm: string) => void
  updateSort: (
    sortBy: FundRegistrySearchParams['sortBy'],
    sortOrder: FundRegistrySearchParams['sortOrder'],
  ) => void
}

export interface FundRegistryProps {
  fundId: string
  effective_at?: Date
}

interface WalletData {
  address: string
  balance: Decimal
  value: Decimal
  maxBalance: Decimal
  transactionCount: number
  firstTransactionAt: string
  lastTransactionAt: string
}

interface UserData {
  id: string
  name: string | null
  totalShares: Decimal
  totalValue: Decimal
  totalTransactions: number
  firstTransactionAt: string
  lastTransactionAt: string
  profileUrl: string | null
  wallets: WalletData[]
}

export function useFundRegistry(
  params: UseFundRegistryParams & { fundId: string },
): UseFundRegistryResult {
  const { fundId } = params
  const [searchParams, setSearchParams] = useState<FundRegistrySearchParams>({
    searchTerm: '',
    sortBy: 'totalShares',
    sortOrder: 'desc',
  })
  const [pagination, setPagination] = useState<FundRegistryPagination>({
    currentPage: 1,
    itemsPerPage: INITIAL_ITEMS_PER_PAGE,
    totalItems: 0,
    hasMore: false,
  })

  const fundQuery = useAdminFund(fundId)
  const tokenAddress = fundQuery.data?.feeders?.[0]?.token_address || null
  const tokenDecimals = fundQuery.data?.feeders?.[0]?.token_decimals || 18
  const tokenHoldersQuery = useTokenHolders({
    tokenAddress: tokenAddress || '',
  })
  const walletAddresses = useMemo(() => {
    if (!tokenHoldersQuery.data) return []
    return tokenHoldersQuery.data.map((holder) => holder.wallet_address)
  }, [tokenHoldersQuery.data])
  const usersQuery = useAdminUsersByWallet({
    walletAddresses,
  })

  const registry = useMemo(() => {
    if (!tokenHoldersQuery.data || !usersQuery.data) return []
    const vaultEntry: [string, AdminUserData][] = fundQuery.data?.feeders?.[0]
      ?.vault?.vault_address
      ? [
          [
            fundQuery.data.feeders[0].vault.vault_address,
            {
              name: fundQuery.data.feeders[0].vault.symbol + ' Vault',
            } as AdminUserData,
          ],
        ]
      : []
    const usersByWallet = usersQuery.data.reduce(
      (map, user) => {
        if (user.wallet_address) {
          map.set(user.wallet_address.toLowerCase(), user)
        }
        return map
      },
      new Map<string, AdminUserData>([...vaultEntry]),
    )

    const userGroups = new Map<string, UserData>()
    tokenHoldersQuery.data.forEach((holder) => {
      const user = usersByWallet.get(holder.wallet_address.toLowerCase())
      const userId = user?.uuid || holder.wallet_address

      if (
        holder.wallet_address == '0x0000000000000000000000000000000000000000'
      ) {
        return
      }

      // todo this incorrectly handles multiple feeders
      const price = fundQuery.data?.feeders?.[0]?.live_nav?.price_per_share || 0
      const balance = new DecimalJS(holder.balance).dividedBy(
        new DecimalJS(10).pow(tokenDecimals),
      )

      const walletData: WalletData = {
        address: holder.wallet_address,
        balance,
        value: balance.mul(new DecimalJS(price)),
        maxBalance: new DecimalJS(holder.max_balance).dividedBy(
          new DecimalJS(10).pow(tokenDecimals),
        ),
        transactionCount: parseInt(holder.transaction_count),
        firstTransactionAt: new Date(
          parseInt(holder.first_transaction_at) * 1000,
        ).toISOString(),
        lastTransactionAt: new Date(
          parseInt(holder.last_transaction_at) * 1000,
        ).toISOString(),
      }

      if (userGroups.has(userId)) {
        const existingUser = userGroups.get(userId)!
        existingUser.wallets.push(walletData)
        existingUser.totalShares = existingUser.totalShares.add(
          walletData.balance,
        )
        existingUser.totalTransactions += walletData.transactionCount
        existingUser.totalValue = existingUser.totalValue.add(walletData.value)
        if (
          Date.parse(walletData.firstTransactionAt) <
          Date.parse(existingUser.firstTransactionAt)
        ) {
          existingUser.firstTransactionAt = walletData.firstTransactionAt
        }
        if (
          Date.parse(walletData.lastTransactionAt) >
          Date.parse(existingUser.lastTransactionAt)
        ) {
          existingUser.lastTransactionAt = walletData.lastTransactionAt
        }
      } else {
        userGroups.set(userId, {
          id: userId,
          name: user?.name || null,
          wallets: [walletData],
          totalShares: new DecimalJS(walletData.balance),
          totalValue: walletData.value,
          totalTransactions: walletData.transactionCount,
          firstTransactionAt: walletData.firstTransactionAt,
          lastTransactionAt: walletData.lastTransactionAt,
          profileUrl: user?.image || null,
        })
      }
    })
    return Array.from(userGroups.values())
  }, [
    fundQuery.data?.feeders,
    tokenDecimals,
    tokenHoldersQuery.data,
    usersQuery.data,
  ])

  // for now this flattens extra wallets and assumes there is one per user
  // todo remove when adding multi-wallet support
  const holders = useMemo((): FundHolder[] => {
    return registry.map((userData) => ({
      id: userData.id,
      name: userData.name,
      walletAddress: userData.wallets[0]?.address || '',
      totalShares: userData.totalShares,
      totalValue: userData.totalValue,
      totalTransactions: userData.totalTransactions,
      firstTransactionAt: userData.firstTransactionAt,
      lastTransactionAt: userData.lastTransactionAt,
      profileUrl: userData.profileUrl,
      isVault: userData.wallets.some(
        (w) =>
          w.address.toLowerCase() ===
          fundQuery.data?.feeders?.[0]?.vault?.vault_address,
      ),
    }))
  }, [fundQuery.data?.feeders, registry])

  const isLoading =
    fundQuery.isLoading || tokenHoldersQuery.isLoading || usersQuery.isLoading
  const isFetching =
    fundQuery.isFetching ||
    tokenHoldersQuery.isFetching ||
    usersQuery.isFetching
  const error = fundQuery.error || tokenHoldersQuery.error || usersQuery.error

  const filteredAndSortedHolders = useMemo(() => {
    let filtered = holders

    if (searchParams.searchTerm) {
      const term = searchParams.searchTerm.toLowerCase()
      filtered = holders.filter(
        (holder) =>
          holder.name?.toLowerCase().includes(term) ||
          holder.walletAddress.toLowerCase().includes(term),
      )
    }

    const sortConfig: Record<
      FundRegistrySearchParams['sortBy'],
      (holder: FundHolder) => string | number
    > = {
      totalShares: (holder) => holder.totalShares.toNumber(),
      totalValue: (holder) => holder.totalValue.toNumber(),
      totalTransactions: (holder) => holder.totalTransactions,
      name: (holder) => holder.name || holder.walletAddress,
      firstTransactionAt: (holder) =>
        new Date(holder.firstTransactionAt).getTime(),
    }

    const getValue = sortConfig[searchParams.sortBy]

    return filtered.sort((a, b) => {
      const aValue = getValue(a)
      const bValue = getValue(b)

      let comparison: number
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else {
        comparison = (aValue as number) - (bValue as number)
      }

      return searchParams.sortOrder === 'asc' ? comparison : -comparison
    })
  }, [holders, searchParams])

  const stats = useMemo((): FundRegistryStats => {
    return {
      totalShares: holders.reduce(
        (sum, holder) => sum.add(holder.totalShares),
        new DecimalJS(0),
      ),
      totalHolders: holders.length,
      totalFrozenTokens: 0,
      totalExceptionCases: 0,
      currentPrice: new DecimalJS(
        fundQuery.data?.feeders?.[0]?.live_nav?.price_per_share || 0,
      ),
    }
  }, [fundQuery.data?.feeders, holders])

  const paginatedHolders = useMemo(() => {
    return filteredAndSortedHolders.slice(0, pagination.itemsPerPage)
  }, [filteredAndSortedHolders, pagination.itemsPerPage])

  const data: FundRegistryData = useMemo(
    () => ({
      stats,
      holders: filteredAndSortedHolders,
    }),
    [stats, filteredAndSortedHolders],
  )

  const loadMore = useCallback(() => {
    setPagination((prev) => {
      const newItemsPerPage = prev.itemsPerPage + LOAD_MORE_ITEMS
      const hasMore = newItemsPerPage < filteredAndSortedHolders.length
      return {
        ...prev,
        itemsPerPage: newItemsPerPage,
        hasMore,
      }
    })
  }, [filteredAndSortedHolders.length])

  const updateSearch = useCallback((searchTerm: string) => {
    setSearchParams((prev) => ({ ...prev, searchTerm }))
    setPagination((prev) => ({
      ...prev,
      itemsPerPage: INITIAL_ITEMS_PER_PAGE,
      hasMore: true,
    }))
  }, [])

  const updateSort = useCallback(
    (
      sortBy: FundRegistrySearchParams['sortBy'],
      sortOrder: FundRegistrySearchParams['sortOrder'],
    ) => {
      setSearchParams((prev) => ({ ...prev, sortBy, sortOrder }))
      setPagination((prev) => ({
        ...prev,
        itemsPerPage: INITIAL_ITEMS_PER_PAGE,
        hasMore: true,
      }))
    },
    [],
  )

  const refetch = useCallback(async () => {
    await Promise.all([
      fundQuery.refetch(),
      tokenHoldersQuery.refetch(),
      usersQuery.refetch(),
    ])
    setPagination((prev) => ({
      ...prev,
      itemsPerPage: INITIAL_ITEMS_PER_PAGE,
      hasMore: filteredAndSortedHolders.length > INITIAL_ITEMS_PER_PAGE,
    }))
  }, [
    fundQuery,
    tokenHoldersQuery,
    usersQuery,
    filteredAndSortedHolders.length,
  ])

  const finalPagination = useMemo(
    () => ({
      ...pagination,
      totalItems: filteredAndSortedHolders.length,
      hasMore: pagination.itemsPerPage < filteredAndSortedHolders.length,
    }),
    [pagination, filteredAndSortedHolders.length],
  )

  return {
    data,
    holders: paginatedHolders,
    stats,
    pagination: finalPagination,
    searchParams,
    isLoading,
    isFetching,
    error,
    refetch,
    loadMore,
    updateSearch,
    updateSort,
  }
}
